export const cn = (...classes) =>
  classes
    .filter(Boolean)
    .join(" ");

/* ==========================================================================
   DATE LOCALE
   ========================================================================== */

export const todayISO = () => {
  const now = new Date();

  const localDate = new Date(
    now.getTime() -
      now.getTimezoneOffset() * 60 * 1000,
  );

  return localDate
    .toISOString()
    .slice(0, 10);
};

/* ==========================================================================
   FORMATAGE
   ========================================================================== */

export function formatNumber(value) {
  return new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

export function formatMaybeNumber(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return "—";
  }

  return formatNumber(value);
}

export function formatDateTime(dateTime) {
  if (!dateTime) {
    return "—";
  }

  try {
    return new Intl.DateTimeFormat(
      "fr-FR",
      {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      },
    ).format(new Date(dateTime));
  } catch {
    return "—";
  }
}

export function getInitials(name = "") {
  const initials = String(name)
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(
      (part) =>
        part[0]?.toUpperCase(),
    )
    .join("");

  return initials || "MA";
}

/* ==========================================================================
   TABLEAUX API
   ========================================================================== */

function getArrayPayload(
  data,
  candidateKeys = [],
) {
  if (Array.isArray(data)) {
    return data;
  }

  for (const key of candidateKeys) {
    if (Array.isArray(data?.[key])) {
      return data[key];
    }
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  if (Array.isArray(data?.rows)) {
    return data.rows;
  }

  if (Array.isArray(data?.results)) {
    return data.results;
  }

  return [];
}

/* ==========================================================================
   NOMBRES
   ========================================================================== */

export function getNumericValue(data) {
  if (
    typeof data === "number" &&
    Number.isFinite(data)
  ) {
    return data;
  }

  const candidates = [
    data?.count,
    data?.total,
    data?.value,
    data?.nombreAdherents,
    data?.nombreInscrits,
    data?.nombreUtilisateurs,
    data?.usersCount,
    data?.adherentsCount,
    data?.inscritsCount,
  ];

  const found = candidates.find(
    (value) =>
      typeof value === "number" &&
      Number.isFinite(value),
  );

  /*
   * Très important :
   * auparavant on retournait 0.
   *
   * Désormais une structure API inconnue vaut null,
   * donc l'interface affiche "—".
   */
  return found ?? null;
}

/* ==========================================================================
   VISITES
   ========================================================================== */

export function getTodayVisits(
  data,
  dateStr,
) {
  const list = getArrayPayload(
    data,
    [
      "visites",
      "visits",
      "items",
    ],
  );

  if (!list.length) {
    return 0;
  }

  const row = list.find((item) => {
    const dateValue =
      item?.date_visite ||
      item?.date ||
      item?.jour ||
      item?.day ||
      "";

    return String(dateValue)
      .startsWith(dateStr);
  });

  return Number(
    row?.compteur ??
      row?.count ??
      row?.total ??
      row?.visits ??
      0,
  );
}

export function getTotalVisitsFromByDate(
  data,
) {
  const list = getArrayPayload(
    data,
    [
      "visites",
      "visits",
      "items",
    ],
  );

  if (!list.length) {
    return null;
  }

  return list.reduce(
    (acc, item) =>
      acc +
      Number(
        item?.compteur ??
          item?.count ??
          item?.total ??
          item?.visits ??
          0,
      ),
    0,
  );
}

/* ==========================================================================
   NOTIFICATIONS
   ========================================================================== */

function getNotificationDate(
  notification = {},
) {
  return (
    notification.created_at ||
    notification.createdAt ||
    notification.date_notification ||
    notification.date ||
    ""
  );
}

function isUnreadNotification(
  notification = {},
) {
  const readValue =
    notification.read_notification ??
    notification.read_notif ??
    notification.is_read ??
    notification.isRead ??
    notification.read ??
    notification.lu ??
    notification.seen ??
    notification.is_seen ??
    notification.read_status;

  /*
   * Certaines anciennes API ne disposent pas de champ de lecture.
   * On préfère alors NE PAS masquer la notification.
   */
  if (
    readValue === undefined ||
    readValue === null
  ) {
    return true;
  }

  if (
    typeof readValue === "boolean"
  ) {
    return readValue === false;
  }

  if (
    typeof readValue === "number"
  ) {
    return readValue === 0;
  }

  const normalized = String(
    readValue,
  )
    .trim()
    .toLowerCase();

  return [
    "0",
    "false",
    "unread",
    "non_lu",
    "non lu",
    "nonlu",
  ].includes(normalized);
}

export function extractNotifications(
  data,
  dateStr,
  {
    onlyToday = false,
    onlyUnread = true,
  } = {},
) {
  const list = getArrayPayload(
    data,
    [
      "notifications",
      "items",
    ],
  );

  let filtered = [
    ...list,
  ];

  /*
   * IMPORTANT :
   * pour la surveillance centrale, onlyToday sera false.
   *
   * Une notification non lue datant d'hier doit continuer
   * d'être visible.
   */
  if (
    onlyToday &&
    dateStr
  ) {
    filtered =
      filtered.filter((item) =>
        String(
          getNotificationDate(item),
        ).startsWith(dateStr),
      );
  }

  if (onlyUnread) {
    filtered =
      filtered.filter(
        isUnreadNotification,
      );
  }

  return filtered.sort(
    (a, b) =>
      new Date(
        getNotificationDate(b) ||
          0,
      ) -
      new Date(
        getNotificationDate(a) ||
          0,
      ),
  );
}

/* ==========================================================================
   MESSAGES
   ========================================================================== */

function getMessageDate(
  message = {},
) {
  return (
    message.created_at ||
    message.createdAt ||
    message.date_message ||
    message.date ||
    ""
  );
}

function isUnreadMessage(
  message = {},
) {
  const readValue =
    message.read_message ??
    message.is_read ??
    message.isRead ??
    message.read ??
    message.lu ??
    message.seen ??
    message.is_seen;

  if (
    readValue === undefined ||
    readValue === null
  ) {
    return true;
  }

  if (
    typeof readValue === "boolean"
  ) {
    return readValue === false;
  }

  if (
    typeof readValue === "number"
  ) {
    return readValue === 0;
  }

  const normalized = String(
    readValue,
  )
    .trim()
    .toLowerCase();

  return [
    "0",
    "false",
    "unread",
    "non_lu",
    "non lu",
    "nonlu",
  ].includes(normalized);
}

export function extractMessages(
  data,
  dateStr,
  {
    mode = "unread",
  } = {},
) {
  const list = getArrayPayload(
    data,
    [
      "messages",
      "items",
    ],
  );

  let filtered =
    list.filter(
      (message) =>
        !message?.is_copy,
    );

  if (
    mode === "today" &&
    dateStr
  ) {
    filtered =
      filtered.filter(
        (message) =>
          String(
            getMessageDate(
              message,
            ),
          ).startsWith(dateStr),
      );
  }

  if (mode === "unread") {
    filtered =
      filtered.filter(
        isUnreadMessage,
      );
  }

  return filtered.sort(
    (a, b) =>
      new Date(
        getMessageDate(b) ||
          0,
      ) -
      new Date(
        getMessageDate(a) ||
          0,
      ),
  );
}

/* ==========================================================================
   RECHERCHE
   ========================================================================== */

export function safeIncludes(
  source,
  search,
) {
  if (!search) {
    return true;
  }

  return String(source || "")
    .toLowerCase()
    .includes(
      String(search)
        .toLowerCase(),
    );
}

/* ==========================================================================
   CSV
   ========================================================================== */

export function downloadCSV(
  filename,
  headers,
  rows,
) {
  const csv =
    headers.join(";") +
    "\n" +
    rows
      .map((row) =>
        row
          .map((cell) =>
            `"${String(
              cell ?? "",
            ).replace(
              /"/g,
              '""',
            )}"`,
          )
          .join(";"),
      )
      .join("\n");

  const blob = new Blob(
    [csv],
    {
      type:
        "text/csv;charset=utf-8;",
    },
  );

  const url =
    URL.createObjectURL(blob);

  const anchor =
    document.createElement("a");

  anchor.href = url;
  anchor.download = filename;

  anchor.click();

  URL.revokeObjectURL(url);
}

/* ==========================================================================
   PAGES
   ========================================================================== */

export function normalizePageName(
  value = "",
) {
  return String(value)
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(
      /[\u0300-\u036f]/g,
      "",
    );
}

export function getHomepageVisitsFromPages(
  data,
  aliases = [],
) {
  const list = getArrayPayload(
    data,
    [
      "pages",
      "visites",
      "visits",
      "items",
    ],
  );

  if (!list.length) {
    return 0;
  }

  const normalizedAliases =
    aliases.map(
      normalizePageName,
    );

  const homeRow =
    list.find((item) => {
      const page =
        normalizePageName(
          item?.page ||
            item?.pathname ||
            item?.url ||
            "",
        );

      return normalizedAliases.includes(
        page,
      );
    });

  return Number(
    homeRow?.total_visits ??
      homeRow?.compteur ??
      homeRow?.count ??
      homeRow?.total ??
      0,
  );
}